import { gf, type GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import { OrderDirection } from "@/admin/graphql.gen/graphql.ts";
import { GraphqlTable, type GqItems, type StandardVariables } from "@/admin/GraphqlTable.tsx";
import { HostDetailView, HostVmsLink } from "@/admin/hosts/HostDetailView.tsx";
import { allHostsWithStorageQueryAtom, type HostWithStorage } from "@/admin/hosts/hostsWithStorageQuery.ts";
import { NumaInfo } from "@/admin/hosts/NumaInfo.tsx";
import { OsBadgeButton } from "@/admin/hosts/OsBadge.tsx";
import { PoolBadgeButton } from "@/admin/hosts/PoolBadge.tsx";
import { StatusBadgeButton } from "@/admin/hosts/StatusBadge.tsx";
import { locationField, numberField } from "@/admin/table_fields/standardFields.tsx";
import { useZodSearchParams } from "@/admin/utils/useZodParse.tsx";
import { apiDatetimeToDate } from "@warrenio/api-spec/conversion";
import { useAtomValue } from "jotai/react";
import { useMemo } from "react";
import { sortBy } from "remeda";
import z from "zod";

function Vmem({ value }: { value: number }) {
    return <>{value.toFixed(1)}&nbsp;GB</>;
}

function Vcpu({ value }: { value: number }) {
    return value.toFixed(1);
}

type HostOrderFields = /* Fake order type since this is not a real query */ string;

const hFields: GqlFieldsOf<HostWithStorage, HostOrderFields> = [
    gf({
        id: "uuid",
        title: "UUID",
        get: (a) => a.uuid,
        copyable: true,
        order: "uuid",
    }),
    gf({
        id: "created_at",
        title: "Created At",
        get: (a) => (a.created_at ? apiDatetimeToDate(a.created_at) : undefined),
        order: "created_at",
    }),
    gf({
        id: "name",
        title: "Name",
        get: (a) => a.name,
        order: "name",
    }),
    gf({
        id: "ip",
        title: "Host/IP",
        get: (a) => a.ip,
        copyable: true,
        order: "ip",
    }),
    gf({
        id: "os",
        title: "OS",
        get: (a) => a.os,
        render: OsBadgeButton,
        order: "os",
    }),
    gf({
        ...numberField,
        id: "usable_vcpu_amount",
        title: "Usable\nvCPU",
        get: (a) => a.usable_vcpu_amount,
        render: Vcpu,
        order: "usable_vcpu_amount",
    }),
    gf({
        ...numberField,
        id: "vcpu_free",
        title: "Free\nvCPU",
        get: (a) => a.vcpu_free,
        render: Vcpu,
        order: "vcpu_free",
    }),
    gf({
        ...numberField,
        id: "usable_vmem_amount",
        title: "Usable\nvMEM",
        get: (a) => a.usable_vmem_amount,
        render: Vmem,
        order: "usable_vmem_amount",
    }),
    gf({
        ...numberField,
        id: "vmem_free",
        title: "Free\nvMEM",
        get: (a) => a.vmem_free,
        render: Vmem,
        order: "vmem_free",
    }),
    gf({
        id: "pool_cpu_model",
        title: "CPU Model",
        get: (a) => a.pool_cpu_model,
        hidden: true,
        order: "pool_cpu_model",
    }),
    gf({
        ...numberField,
        id: "numa_node_count",
        title: "NUMA\nNodes",
        get: (a) => a.cpu_info?.nodes?.length ?? -1, // Fallback for sorting
        render: NumaInfo,
        order: "numa_node_count",
    }),
    gf({
        id: "pool_uuid",
        title: "Pool UUID",
        get: (a) => a.pool_uuid,
        copyable: true,
        hidden: true,
        order: "pool_uuid",
    }),
    gf({
        id: "pool_name",
        title: "Pool Name",
        get: (a) => a.pool_name,
        render: PoolBadgeButton,
        order: "pool_name",
    }),
    gf({
        id: "storage_pool_uuid",
        title: "Storage Pool UUID",
        get: (a) => a.storage_pool_uuid,
        copyable: true,
        hidden: true,
        order: "storage_pool_uuid",
    }),
    gf({
        id: "storage_pool_name",
        title: "Storage Pool\nName",
        get: (a) => a.storage_pool_name,
        order: "storage_pool_name",
    }),
    gf({
        id: "resources",
        title: "VMs",
        get: (a) => a,
        render: ({ item }) => <HostVmsLink uuid={item.uuid} />,
    }),
    gf({
        id: "status",
        title: "Status",
        get: (a) => a.status,
        render: StatusBadgeButton,
        order: "status",
    }),
    gf({
        ...locationField,
        get: (a) => a.location,
        order: "location",
    }),
];

const SearchSchema = z
    .object({
        hostPoolId: z.string(),
    })
    .partial();

function matchesSearch(item: HostWithStorage, search: string) {
    return (
        item.uuid.toLowerCase().includes(search) ||
        item.name.toLowerCase().includes(search) ||
        item.ip.toLowerCase().includes(search) ||
        item.pool_name.toLowerCase().includes(search) ||
        item.storage_pool_name.toLowerCase().includes(search)
    );
}

// TODO: Fake query, support non-GraphQL data in a better way too
function useHostsQuery(vars: StandardVariables<HostOrderFields>): GqItems<HostWithStorage> {
    const { hostPoolId } = useZodSearchParams(SearchSchema) ?? {};

    const { data, isPending, error, refetch } = useAtomValue(allHostsWithStorageQueryAtom);

    const { orderDir, orderField, search, locations } = vars;

    // XXX: Hack to sort and filter the data since we don't have a real GraphQL query
    const filteredData = useMemo(() => {
        if (!data) {
            return undefined;
        }

        let items = data;
        // Filter by host pool ID if provided in the query parameters
        if (hostPoolId) {
            items = items.filter((item: HostWithStorage) => item.pool_uuid === hostPoolId);
        }

        if (locations) {
            items = items.filter((item) => locations.includes(item.location));
        }

        // Free text search
        if (search) {
            const normalizedSearch = search.toLowerCase();
            items = items.filter((item) => matchesSearch(item, normalizedSearch));
        }

        const orderInfo = orderField ? hFields.find((f) => f.order === orderField) : undefined;
        // Sort by the selected field
        if (orderInfo) {
            // Use the field's `get` accessor for the actual value to sort by
            items = sortBy(items, [
                (item) => orderInfo.get(item) as string | number | Date,
                orderDir === OrderDirection.Desc ? "desc" : "asc",
            ]);
        }

        return { items };
    }, [data, hostPoolId, orderField, orderDir, search, locations]);

    return { data: filteredData, loading: isPending, error, refetch };
}

export function HostsTable() {
    return (
        <GraphqlTable
            title="Hypervisors"
            fields={hFields}
            getId={(item) => item.uuid}
            useQuery={useHostsQuery}
            renderDetail={(item) => <HostDetailView key={item.uuid} item={item} />}
        />
    );
}
